/**
 *  多种优惠劵优惠计算
 */

import { getDishPrice, getDishesCount } from '../dish-helper.js';
import { getDishBenefitPrice, isDishGiftCoupon } from '../order-helper';
import { CouponUseTypes, CouponTypes, MemberPrivTypes } from '../const.js';
import { getCanUsePrivDishes } from './common';

function initRealPrice(orderedDishes, calcPriv) {
  const realPrices = {};

  orderedDishes.forEach((dish) => {
    const { id } = dish;
    realPrices[id] = Math.max(getDishPrice(dish) - calcPriv(dish), 0);
  });

  return realPrices;
}

function distributeBasePrices({ activityBenefit, activityBenefitNum, marketPrice, count }) {
  const realBasePrices = Array.apply(null, { length: count }).map(item => marketPrice);

  if (activityBenefit <= 0) {
    return realBasePrices;
  }

  const privAmount = activityBenefit / activityBenefitNum;
  let i = 0;
  while (i < activityBenefitNum) {
    realBasePrices[i] = Math.max(marketPrice - privAmount, 0);
    i++;
  }

  return realBasePrices;
}

function calcRealBasePricesWhenMulitPriv(dishes, multiPrivilegePrices) {
  const ret = {};

  dishes.forEach(dish => {
    const { id, marketPrice } = dish;
    const count = getDishesCount([dish]);
    const { prices } = multiPrivilegePrices[id] || { prices: [] };
    const activityBenefitNum = count;
    let activityBenefit = 0;

    if (prices.length) {
      activityBenefit = prices.reduce((acc, cur) => acc + cur, 0);
    }

    ret[id] = distributeBasePrices({ count, marketPrice, activityBenefit, activityBenefitNum });
  });

  return ret;
}

function calcRealBasePricesWhenSinglePriv(dishes, discountProps) {
  const ret = {};

  const distributeBasePricesWhenSinglePriv = (dish) => {
    const { order, marketPrice } = dish;

    if (Array.isArray(order)) {
      return order.reduce((arr, cur) => {
        arr = arr.concat(distributeBasePrices({ ...cur, marketPrice }));
        return arr;
      }, []).asMutable({ deep: true });
    }

    return distributeBasePrices({ ...dish, count: order });
  };

  const distributeBasePricesWhenMemberPrice = (dish) => {
    const { marketPrice } = dish;
    const { discountList, discountType, discount } = discountProps;
    const count = getDishesCount([dish]);
    const dishDiscount = discountList.find(item => item.dishId === dish.id);
    const discountValue =
      discountType === MemberPrivTypes.UNIFIED_DISCOUNT ? discount : ((dishDiscount || {}).value);
    let basePrice = marketPrice;
    const memberPriceStr = (discountValue !== null && discountValue !== undefined) ? discountValue.toString() : null;  // 消除memberPrice为0的影响
    // if (discountValue) {
    if (memberPriceStr) {
      basePrice = (discountType === MemberPrivTypes.DISCOUNT || discountType === MemberPrivTypes.UNIFIED_DISCOUNT)
        ? (marketPrice * discountValue / 10)
        : discountType === 2
          ? (marketPrice - discountValue)
          : marketPrice;
    }
    return Array.apply(null, { length: count }).map(() => basePrice);
  };

  dishes.forEach(dish => {
    const { id, noUseDiscount, isMember } = dish;
    const isSupportMemberPrice = isMember && !noUseDiscount;

    ret[id] = isSupportMemberPrice
      ? distributeBasePricesWhenMemberPrice(dish)
      : distributeBasePricesWhenSinglePriv(dish);
  });

  return ret;
}

function initRealPriceDiffMultiPriv(orderedDishes, multiPrivilegePrices) {
  const realPrices = initRealPrice(orderedDishes, (dish) => {
    const { prices } = multiPrivilegePrices[dish.id] || { prices: [] };

    return prices.reduce((acc, cur) => acc + cur, 0);
  });

  return {
    realPrices,
    realBasePrices: calcRealBasePricesWhenMulitPriv(orderedDishes, multiPrivilegePrices),
  };
}

function initRealPriceDiffSinglePriv(orderedDishes, serviceProps) {
  const realPrices = initRealPrice(orderedDishes, (dish) => {
    return getDishBenefitPrice(dish, serviceProps);
  });

  return {
    realPrices,
    realBasePrices: calcRealBasePricesWhenSinglePriv(orderedDishes, serviceProps.discountProps),
  };
}

function getCouponValue(coupon, key) {
  const { coupRuleBeanList, weixinValue } = coupon;
  if (weixinValue) {
    return +weixinValue || 0;
  }

  const currentCoupon = coupRuleBeanList.filter(couponDetaile => couponDetaile.ruleName === key)[0];

  return Number(currentCoupon && currentCoupon.ruleValue) || 0;
}

function totalRealPrices(realPrices) {
  return Object.keys(realPrices).reduce((acc, key) => acc + realPrices[key], 0);
}

function mergeCouponPrivilegePrices(masterPrices, prices) {
  const ret = { ...masterPrices };

  Object.keys(prices).forEach(key => {
    ret[key] = (ret[key] || []).concat(prices[key]);
  });

  return ret;
}

const couponPriv = {
  getCalcOption(coupon, serviceProps) {
    const { privateRoom } = serviceProps.couponsProps;
    const { coupDishBeanList } = coupon;

    const options = {
      [CouponTypes.ZKVALUE]: {
        ruleName: 'zkValue',
        calcExpression: ({ price, value }) => price * (1 - value / 10),
      },

      [CouponTypes.OFFERVALUE]: {
        ruleName: 'offerValue',
        calcExpression: ({ price, value, total }) => Math.min(value * (price / total), price),
      },

      [CouponTypes.FACEVALUE]: {
        ruleName: 'faceValue',
        calcExpression: ({ price, value, total }) => Math.min(value * (price / total), price),
      },

      // 菜品券优先计算，仅仅针对菜品的基准价
      [CouponTypes.GIFTVALUE]: {
        ruleName: 'giftName',
        calcExpression: ({ dish, currentBasePrices }) => {
          if (privateRoom || !coupDishBeanList.length || !currentBasePrices.length) {
            return 0;
          }

          const [firstCouponDish] = coupDishBeanList;
          if (dish.brandDishId !== firstCouponDish.dishId) {
            return 0;
          }

          let price = 0;
          while (!price && currentBasePrices.length) {
            price = currentBasePrices.shift();
          }

          return price;
        },
      },
    };

    const ret = options[coupon.couponType];
    if (privateRoom) {
      ret.couponValue = coupon.faceValue || 0;
    }

    return ret;
  },

  calc({ coupon, subRealPrices, serviceProps, dishes, realBasePrices }) {
    const ret = {};
    const { ruleName, calcExpression, couponValue } = this.getCalcOption(coupon, serviceProps);
    const value = couponValue != null ? couponValue : getCouponValue(coupon, ruleName);
    const total = totalRealPrices(subRealPrices) || 1;

    Object.keys(subRealPrices).forEach(id => {
      const realPrice = subRealPrices[id] || 0;
      const currentBasePrices = realBasePrices[id] || [];
      const dish = dishes.filter(item => item.id === id)[0];
      const subtraction = ret[id] = calcExpression({
        price: realPrice,
        value,
        total,
        dish,
        currentBasePrices,
      });

      subRealPrices[id] = Math.max(realPrice - subtraction, 0);
    });
    return ret;
  },
};

function initRealPrices(orderedDishes, serviceProps) {
  const { wholeOrderBenefit } = serviceProps;
  const { privilegePrices: multiPrivilegePrices } = serviceProps.activityBenefit;
  const isMultiPrivilege = wholeOrderBenefit && wholeOrderBenefit.isChecked;

  return isMultiPrivilege
      ? initRealPriceDiffMultiPriv(orderedDishes, multiPrivilegePrices)
      : initRealPriceDiffSinglePriv(orderedDishes, serviceProps);
}

export default {
  /**
   * 排序规则 菜品礼品券<部分可用优惠券<全部可用优惠券
   */
  sortCoupons(coupons) {
    const dishGiftCoupons = [];
    const others = [];

    coupons.forEach(coupon => {
      if (isDishGiftCoupon(coupon)) {
        dishGiftCoupons.push(coupon);
      } else {
        others.push(coupon);
      }
    });

    const unlimitCoupons = [];
    const limitCoupons = [];

    others.forEach(coupon => {
      if (coupon.dishConditionType === CouponUseTypes.ALL) {
        unlimitCoupons.push(coupon);
      } else {
        limitCoupons.push(coupon);
      }
    });

    return [].concat(dishGiftCoupons, limitCoupons, unlimitCoupons);
  },

  /**
   * 获取菜品礼品券和菜品的关联
   * @param {Array} orderedDishes
   * @param {Object} serviceProps
   */
  getGiftCouponToDishRelations(orderedDishes, serviceProps) {
    const result = {};
    const { currentCouponsDetail: coupons } = serviceProps.couponsProps;

    if (!coupons.length || !orderedDishes.length) {
      return result;
    }

    const giftCoupons = coupons.filter(coupon => isDishGiftCoupon(coupon));
    if (!giftCoupons.length) {
      return result;
    }

    const { realBasePrices } = initRealPrices(orderedDishes, serviceProps);

    const distributeCouponIds = (dish, validCouponIds) => {
      const { id, order } = dish;
      const currentBasePrices = realBasePrices[id];
      const couponIds = Array.apply(null, { length: currentBasePrices.length }).map(item => '');

      currentBasePrices.forEach((price, i) => {
        if (price > 0 && validCouponIds.length) {
          const couponId = validCouponIds.shift();

          couponIds[i] = couponId;
          currentBasePrices[i] = 0;
        }
      });

      if (Array.isArray(order)) {
        let start = 0;

        return order.map((orderItem, index) => {
          const { count } = orderItem;
          const end = start + count;
          const ids = couponIds.slice(start, end).filter(item => !!item);

          start = end;

          return ids;
        }).asMutable({ deep: true });
      }

      return [couponIds.filter(item => !!item)];
    };

    orderedDishes.forEach(dish => {
      const { brandDishId } = dish;
      const validCouponIds = [];

      giftCoupons.forEach(coupon => {
        const [first] = coupon.coupDishBeanList;
        if (first.dishId !== brandDishId) {
          return;
        }

        validCouponIds.push(coupon.id);
      });

      if (validCouponIds.length) {
        result[dish.id] = distributeCouponIds(dish, validCouponIds);
      }
    });

    return result;
  },

  getAfterCouponPrices(orderedDishes, serviceProps) {
    const { currentCouponsDetail: coupons } = serviceProps.couponsProps;
    if (!orderedDishes.length || !coupons.length) {
      return [];
    }

    let ret = {};
    const { realPrices, realBasePrices } = initRealPrices(orderedDishes, serviceProps);

    this.sortCoupons(coupons.asMutable({ deep: true })).forEach((coupon) => {
      const dishes = getCanUsePrivDishes(orderedDishes, coupon);
      if (!dishes.length) {
        return;
      }

      const subRealPrices = dishes.reduce((obj, dish) => {
        obj[dish.id] = realPrices[dish.id];
        return obj;
      }, {}).asMutable();
      const couponPrivilegePrices = couponPriv.calc({
        subRealPrices,
        coupon,
        serviceProps,
        dishes,
        realBasePrices,
      });

      Object.assign(realPrices, subRealPrices);
      ret = mergeCouponPrivilegePrices(ret, couponPrivilegePrices);
    });

    return ret;
  },

  calc(orderedDishes, serviceProps) { // 计算优惠金额
    const ret = this.getAfterCouponPrices(orderedDishes, serviceProps);

    return Object.keys(ret).reduce((acc, key) => {
      return acc + ret[key].reduce((sum, cur) => sum + cur, 0);
    }, 0);
  },

  getRealPrices(orderedDishes, serviceProps) {
    return initRealPrices(orderedDishes, serviceProps);
  },
};



// WEBPACK FOOTER //
// ./src/helper/privilege/coupon-priv-calculator.js